import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { TabPanel, TabList, Tab, Tabs } from 'ui-components/components/tabs';
import { Text, TextType } from 'ui-components/components/text';

import { useEnhancedReturnsCountContext } from '../enhanced-returns-count-context';
import { EnhancedReturnsCompleted } from './grids/completed-container';
import { useEnhancedReturnsContext } from './grids/enhanced-returns-context';
import { EnhancedReturnsNeedsAttention } from './grids/needs-attention-container';
import { EnhancedReturnsPendingApproval } from './grids/pending-approval-container';
import { EnhancedReturnsPendingPayment } from './grids/pending-payment-container';
import { viewMessages } from './messages';
import styles from './styles.module.less';
import { RETURNS_GRID_SELECT_STATUS_TAB } from '../../../constants/analytics-events';
import { useEnableEnhancedReturnsErrors } from '../../../feature-flags/hooks/use-enable-enhanced-returns-errors';
import { Segment } from '../../../services/analytics-services/segment';
import {
  PATH_ENHANCED_RETURNS,
  STATUS_COMPLETED,
  STATUS_PENDING_APPROVAL,
  STATUS_PENDING_PAYMENT,
  NEEDS_ATTENTION,
  useEnhancedReturnRouteMatch,
  resetPageQueryParam,
} from '../../../store/returns/returns.helpers';

export const EnhancedReturnStatusTabs = () => {
  const history = useHistory();
  const { storeGuid, statusViewId } = useEnhancedReturnRouteMatch();
  const { countsPerStoreMap } = useEnhancedReturnsCountContext();
  const { isStoreSetupCompleteForEnhancedReturns } = useEnhancedReturnsContext();
  const isEnhancedReturnsErrorsEnabled = useEnableEnhancedReturnsErrors();
  const searchResultsForStore = countsPerStoreMap[storeGuid];
  const statusViewCountMap = searchResultsForStore?.statusViewCountMap || {};

  const shouldShowCount = isStoreSetupCompleteForEnhancedReturns;

  // Tab mapping changes based on feature flag
  const tabIndexToStatusViewIdMap = useMemo(
    () =>
      isEnhancedReturnsErrorsEnabled
        ? {
            0: NEEDS_ATTENTION,
            1: STATUS_PENDING_APPROVAL,
            2: STATUS_PENDING_PAYMENT,
            3: STATUS_COMPLETED,
          }
        : {
            0: STATUS_PENDING_APPROVAL,
            1: STATUS_PENDING_PAYMENT,
            2: STATUS_COMPLETED,
          },
    [isEnhancedReturnsErrorsEnabled],
  );

  const getStatusFromTabIndex = useCallback((index) => tabIndexToStatusViewIdMap[index], [tabIndexToStatusViewIdMap]);
  const getTabIndexFromStatus = useCallback(
    (status) => {
      const stringyIndex = Object.keys(tabIndexToStatusViewIdMap).find((key) => tabIndexToStatusViewIdMap[key] === status);
      return stringyIndex ? parseInt(stringyIndex, 10) : 0;
    },
    [tabIndexToStatusViewIdMap],
  );

  const selectedTabIndex = getTabIndexFromStatus(statusViewId);

  const changeView = useCallback(
    (tabIndex) => {
      const viewId = getStatusFromTabIndex(tabIndex);

      Segment.trackEvent(RETURNS_GRID_SELECT_STATUS_TAB, {
        type: RETURNS_GRID_SELECT_STATUS_TAB,
        viewId,
      });

      const newParams = resetPageQueryParam(history.location.search);
      history.push({
        pathname: `${PATH_ENHANCED_RETURNS}/${storeGuid}/${viewId}`,
        search: newParams,
      });
    },
    [history, storeGuid, getStatusFromTabIndex],
  );

  // Build tab props based on feature flag
  const tabProps = [];

  if (isEnhancedReturnsErrorsEnabled) {
    tabProps.push({
      id: NEEDS_ATTENTION,
      name: <FormattedMessage {...viewMessages.needsAttention} />,
      count: statusViewCountMap[NEEDS_ATTENTION] ?? 0,
    });
  }

  tabProps.push(
    {
      id: STATUS_PENDING_APPROVAL,
      name: <FormattedMessage {...viewMessages.pendingApproval} />,
      count: statusViewCountMap[STATUS_PENDING_APPROVAL] ?? 0,
    },
    {
      id: STATUS_PENDING_PAYMENT,
      name: <FormattedMessage {...viewMessages.pendingPayment} />,
      count: statusViewCountMap[STATUS_PENDING_PAYMENT] ?? 0,
    },
    {
      id: STATUS_COMPLETED,
      name: <FormattedMessage {...viewMessages.completed} />,
      count: statusViewCountMap[STATUS_COMPLETED] ?? 0,
    },
  );

  return (
    <Tabs className={styles.tabs} onSelectedTabIndexChange={changeView} selectedTabIndex={selectedTabIndex}>
      <TabList className={styles['tab-list']}>
        {tabProps.map((tab, index) => (
          <Tab key={tab.id}>
            {tab.name}
            {shouldShowCount ? (
              <Text
                className={classnames(styles['tab-count'], {
                  [styles.selected]: selectedTabIndex === index,
                })}
                textType={TextType.Caption}
                value={tab.count}
              />
            ) : null}
          </Tab>
        ))}
      </TabList>
      {isEnhancedReturnsErrorsEnabled ? (
        <TabPanel selectedClassName={styles.tabs}>
          <EnhancedReturnsNeedsAttention />
        </TabPanel>
      ) : null}
      <TabPanel selectedClassName={styles.tabs}>
        <EnhancedReturnsPendingApproval />
      </TabPanel>
      <TabPanel selectedClassName={styles.tabs}>
        <EnhancedReturnsPendingPayment />
      </TabPanel>
      <TabPanel selectedClassName={styles.tabs}>
        <EnhancedReturnsCompleted />
      </TabPanel>
    </Tabs>
  );
};
